import {
  AccessControlProvider,
  AuthProvider,
  Refine,
} from '@pankod/refine-core';
import { useMemo } from 'react';
import { KBarProvider } from '@pankod/refine-kbar';
import {
  ErrorComponent,
  notificationProvider,
  Layout,
  GlobalStyles,
  CssBaseline,
  RefineSnackbarProvider,
} from '@pankod/refine-mui';
import { LoginPage } from './pages/auth';
import routerProvider, {
  Routes,
  Route,
} from '@pankod/refine-react-router-v6';

import { useTranslation } from 'react-i18next';
import { FeaturedVideo } from '@mui/icons-material';

import {
  fbAuth,
  fbFirestore,
  firebaseAuth,
  firestoreDatabase,
} from './lib/firebaseService';
import {
  firebaseAppDefault as livenowFirebaseAppDefault,
  firebaseAuth as livenowFirebaseAuth,
  firestoreDatabase as livenowFirestoreDatabase,
} from './lib/livenowFirebaseService';

import { FrameList, FrameCreate, FrameEdit } from 'pages/frames';
import { ColorModeContextProvider } from 'contexts';
import { Header, Title, Footer, Sider } from 'components';
import {
  BackgroundCreate,
  BackgroundEdit,
  BackgroundList,
} from 'pages/backgrounds';
import { VtuberCreate, VtuberEdit, VtuberList } from 'pages/vtubers';
import {
  OfferCodeCreate,
  OfferCodeEdit,
  OfferCodeList,
  ManagerOfferCodesList,
} from 'pages/offers';
import { DeviceUsingList } from 'pages/offers/device-usings/list';
import { DeviceUsingEdit } from 'pages/offers/device-usings/edit';
import { UserPointCreate, UserPointList } from 'pages/user/points';
import { UserList } from 'pages/user';
import {
  ManagerCreate,
  ManagerEdit,
  ManagerList,
} from 'pages/managers';
import {
  ADMIN_USERS_RESOURCE,
  getRoleByEmail,
} from 'services/adminUserService';
import {
  collection,
  getDocs,
  getFirestore,
  query,
  orderBy,
  limit,
  QueryConstraint,
  getCountFromServer,
  where,
} from 'firebase/firestore';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { AdminUserRole } from 'interfaces';
import { CONST } from 'config/const';

const PAGE_PERMISSIONS: Record<string, AdminUserRole[]> = {
  Offers: ['admin', 'manager'],
};

const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const i18nProvider = {
    translate: (key: string, params: object) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };
  const authProvider: AuthProvider = useMemo(() => {
    const auth1 = firebaseAuth.getAuthProvider();
    const auth2 = livenowFirebaseAuth.getAuthProvider();
    return {
      login: async (params: any) => {
        await auth1.login(params);
        await auth2.login(params);

        const userCredential = await signInWithEmailAndPassword(
          fbAuth,
          params.email,
          params.password,
        );
        await fbAuth.updateCurrentUser(userCredential.user);
        let role: AdminUserRole | null = null;
        if (userCredential.user) {
          role = await getRoleByEmail(
            userCredential.user.email || '',
          );
        }
        const redirectTo = role === 'admin' ? '/Frames' : '/Offers';

        return redirectTo;
      },
      logout: async (params: any) => {
        const isSuccess1 = await auth1.logout(params);
        const isSuccess2 = await auth2.logout(params);
        return isSuccess1 && isSuccess2;
      },
      checkError: async (params: any) => {
        await auth1.checkError(params);
        await auth2.checkError(params);
      },
      checkAuth: async (params: any) => {
        await auth1.checkAuth(params);
        await auth2.checkAuth(params);
        return;
      },
      getPermissions: async (params?: any) => {
        await auth1.getPermissions?.(params);
        await auth2.getPermissions?.();

        const currentEmail = fbAuth.currentUser?.email || '';
        if (!currentEmail) {
          return { role: 'guest' };
        }

        const adminUsersRef = collection(
          fbFirestore,
          ADMIN_USERS_RESOURCE,
        );
        const adminUsersSnapshot = await getDocs(
          query(
            adminUsersRef,
            where('email', '==', currentEmail.toLowerCase()),
            limit(1),
          ),
        );

        if (adminUsersSnapshot.empty) {
          return { role: 'guest' };
        }

        const user = adminUsersSnapshot.docs[0].data();
        const userRole =
          user.status === CONST.ADMIN_STATUS.INACTIVE
            ? 'guest'
            : user.role || 'guest';
        return { role: userRole };
      },
      getUserIdentity: async (params?: any) => {
        const userIdentity1 = await auth1.getUserIdentity?.(params);
        return userIdentity1;
      },
    };
  }, []);

  const accessControlProvider: AccessControlProvider = useMemo(
    () => ({
      can: async ({ resource }) => {
        const permissions = await authProvider.getPermissions?.();
        const role = permissions?.role || 'guest';
        const pagePermissions = PAGE_PERMISSIONS[resource] || [
          'admin',
        ];

        return {
          can: pagePermissions.includes(role),
          reason: `Only ${pagePermissions.join(' and ')} can access this page`,
        };
      },
    }),
    [authProvider],
  );
  const _livenowFirestoreDataProvider = useMemo(() => {
    const dataProvider = livenowFirestoreDatabase.getDataProvider();

    return {
      ...dataProvider,
      getList: async (
        params: Parameters<typeof dataProvider.getList>[0],
      ) => {
        if (params.resource !== 'users') {
          return dataProvider.getList(params);
        }

        const { filters } = params;
        const hasOr = filters?.some(
          (filter) => filter.operator === 'or',
        );
        if (!hasOr) {
          const size = 20;
          const q: QueryConstraint[] = [];
          const db = getFirestore(livenowFirebaseAppDefault);
          const ref = collection(db, 'users');

          q.push(orderBy('created_at', 'desc'));
          q.push(limit(size));

          const snapshot = await getDocs(query(ref, ...q));

          const data = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          const totalRef = await getCountFromServer(ref);

          return {
            data,
            total: totalRef.data().count,
          };
        }
        const filterOrItems = filters?.[0].value as any[];
        const filterList: any[] = [];
        filterOrItems.filter((filter) => {
          if (filter.operator === 'and') {
            filterList.push(filter.value);
          } else {
            filterList.push(filter);
          }
        });
        const response = await Promise.all(
          filterList.map((filters) =>
            dataProvider.getList({ ...params, filters }),
          ),
        );
        const _res = response.reduce(
          (acc, res) => {
            return {
              data: [...acc.data, ...res.data],
              total: acc.total + (res.total || 0),
            };
          },
          {
            data: [],
            total: 0,
          },
        );

        return _res;
      },
      getOne: async (
        params: Parameters<typeof dataProvider.getOne>[0],
      ) => {
        return dataProvider.getOne(params);
      },
    };
  }, []);

  return (
    <KBarProvider>
      <ColorModeContextProvider>
        <CssBaseline />
        <GlobalStyles
          styles={{ html: { WebkitFontSmoothing: 'auto' } }}
        />
        <RefineSnackbarProvider>
          <Refine
            routerProvider={{
              ...routerProvider,
            }}
            dataProvider={{
              default: firestoreDatabase.getDataProvider(),
              livenow: _livenowFirestoreDataProvider,
            }}
            authProvider={authProvider}
            accessControlProvider={accessControlProvider}
            i18nProvider={i18nProvider}
            Title={Title}
            Sider={Sider}
            ReadyPage={LoginPage}
            Layout={Layout}
            Header={Header}
            Footer={Footer}
            LoginPage={LoginPage}
            catchAll={<ErrorComponent />}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
            }}
            notificationProvider={notificationProvider}
            resources={[
              {
                name: 'Frames',
                list: FrameList,
                icon: <FeaturedVideo />,
                create: FrameCreate,
                edit: FrameEdit,
                options: {
                  label: 'Frames',
                },
              },
              {
                name: 'Vtubers',
                list: VtuberList,
                icon: <FeaturedVideo />,
                create: VtuberCreate,
                edit: VtuberEdit,
                options: {
                  label: 'Vtubers',
                },
              },
              {
                name: 'Backgrounds',
                list: BackgroundList,
                icon: <FeaturedVideo />,
                create: BackgroundCreate,
                edit: BackgroundEdit,
                options: {
                  label: 'Backgrounds',
                },
              },
              {
                name: 'Offers',
                list: OfferCodeList,
                icon: <FeaturedVideo />,
                create: OfferCodeCreate,
                edit: OfferCodeEdit,
                options: {
                  label: 'Offers',
                },
              },
              {
                name: 'device_usings',
                list: DeviceUsingList,
                edit: DeviceUsingEdit,
                icon: <FeaturedVideo />,
                options: {
                  hide: true,
                },
              },
              {
                name: 'users',
                list: UserList,
                show: UserPointList,
                icon: <FeaturedVideo />,
                options: {
                  label: 'Users',
                  dataProviderName: 'livenow',
                },
              },
              {
                name: 'points',
                icon: <FeaturedVideo />,
                create: UserPointCreate,
                options: {
                  label: 'User Point',
                  dataProviderName: 'livenow',
                },
              },
              {
                name: 'admin_users',
                list: ManagerList,
                create: ManagerCreate,
                edit: ManagerEdit,
                icon: <FeaturedVideo />,
                options: {
                  label: 'Admin Users',
                },
              },
              {
                name: 'manager_offer_codes',
                list: ManagerOfferCodesList,
                icon: <FeaturedVideo />,
                options: {
                  label: 'Manager Invite Codes',
                },
              },
            ]}
          />
        </RefineSnackbarProvider>
      </ColorModeContextProvider>
    </KBarProvider>
  );
};

export default App;
